import { z } from 'zod'
import type { ApiClient } from '../apiClient'
import { DimNotApplicableError } from '../types'
import { clampFromToFloor, getModelFloorIso } from '~/state/modelFloor'

/** Single query-boundary enforcement of the ≥model-floor cutoff for every walk. */
function resolveStartday(from: string): string {
  return clampFromToFloor(from, getModelFloorIso())
}

/**
 * FU-m: detect the "group-by over a dim the fact lacks" shape. PG groups over the missing
 * key and returns dims:{<dim>:null}. Returns the distinct null-keyed dim names, or null if none.
 * Only inspects dims KEYS — a null VALUE (ratio zero-denominator) is NOT flagged.
 */
export function detectInapplicableGroupBy(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null
  const dims = new Set<string>()
  for (const row of raw) {
    const d = (row as { dims?: unknown } | null)?.dims
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      for (const [k, v] of Object.entries(d as Record<string, unknown>)) {
        if (v === null) dims.add(k)
      }
    }
  }
  return dims.size > 0 ? [...dims] : null
}

export interface WalkTimelineParams {
  fact_table: string
  kpi: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
  group_by?: string[]
  bucket_size?: 'day' | 'week' | 'month' | 'quarter' | 'year'
  aggregation?: 'sum' | 'avg' | 'max' | 'min'
}

export const walkTimelineRowSchema = z.object({
  kpi_type: z.string(),
  time: z.string(),
  value_type: z.string().nullable(),
  // dim values may be null when a side can't carry a dim (mig 0080); ratio walks render, volume walks detect-and-throw via detectInapplicableGroupBy.
  dims: z.record(z.string().nullable()).nullable(),
  // Ratio walks return NULL when the denominator sums to 0 (e.g. Sales CpO on a bucket/slice
  // with 0 orders). NULL = "undefined ratio" → the chart renders a gap (pivots map it to
  // undefined). Sum walks never produce NULL, so this is permissive-but-correct for them.
  value: z.number().nullable(),
  // #11: ratio walks (walk_ratio) also return the per-bucket numerator (scaled) + denominator so the
  // FE can cumulate Σnum/Σdenom. Absent on volume walks; PostgREST may send null → nullish→undefined.
  numerator: z
    .number()
    .nullish()
    .transform((v) => v ?? undefined),
  denominator: z
    .number()
    .nullish()
    .transform((v) => v ?? undefined),
})
export type WalkTimelineRow = z.infer<typeof walkTimelineRowSchema>

export async function walkTimeline(
  client: ApiClient,
  p: WalkTimelineParams,
  signal?: AbortSignal,
): Promise<WalkTimelineRow[]> {
  const raw = await client.rpc<unknown>(
    'walk_timeline',
    {
      p_fact_table: p.fact_table,
      p_kpi: p.kpi,
      p_startday: resolveStartday(p.from),
      p_endday: p.to,
      p_bucketsize: p.bucket_size ?? 'month',
      p_group_by: p.group_by ?? [],
      p_filter_dims: p.filter_dims ?? {},
      p_aggregation: p.aggregation ?? 'sum',
    },
    { signal },
  )
  const inapplicable = detectInapplicableGroupBy(raw)
  if (inapplicable) throw new DimNotApplicableError(inapplicable)
  return z.array(walkTimelineRowSchema).parse(raw)
}

export interface WalkAbsoluteParams {
  fact_table: string
  kpi: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
  group_by?: string[]
  aggregation?: 'sum' | 'avg' | 'max' | 'min'
}

export const walkAbsoluteRowSchema = z.object({
  kpi_type: z.string(),
  value_type: z.string().nullable(),
  // dim values may be null when a side can't carry a dim (mig 0080); ratio walks render, volume walks detect-and-throw via detectInapplicableGroupBy.
  dims: z.record(z.string().nullable()).nullable(),
  // See walkTimelineRowSchema.value — ratio walks can return NULL (zero-denominator slice).
  value: z.number().nullable(),
})
export type WalkAbsoluteRow = z.infer<typeof walkAbsoluteRowSchema>

export async function walkAbsolute(
  client: ApiClient,
  p: WalkAbsoluteParams,
  signal?: AbortSignal,
): Promise<WalkAbsoluteRow[]> {
  const raw = await client.rpc<unknown>(
    'walk_absolute',
    {
      p_fact_table: p.fact_table,
      p_kpi: p.kpi,
      p_startday: resolveStartday(p.from),
      p_endday: p.to,
      p_group_by: p.group_by ?? [],
      p_filter_dims: p.filter_dims ?? {},
      p_aggregation: p.aggregation ?? 'sum',
    },
    { signal },
  )
  const inapplicable = detectInapplicableGroupBy(raw)
  if (inapplicable) throw new DimNotApplicableError(inapplicable)
  return z.array(walkAbsoluteRowSchema).parse(raw)
}

// --- Ratio walks (Phase B Sales ratios) -------------------------------------
// The ratio RPCs (api.walk_ratio / api.walk_ratio_absolute) take the kpi string
// as `p_ratio_kpi` and resolve their own numerator/denominator facts internally
// from api_internal.ratio_metric_config. There is intentionally NO fact_table and
// NO aggregation argument — unlike walk_timeline/walk_absolute. The chart_registry
// row's default_params.fact_table is informational only and must NOT be forwarded.
// Row shapes are identical to walkTimelineRow / walkAbsoluteRow respectively.

export interface WalkRatioParams {
  kpi: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
  group_by?: string[]
  bucket_size?: 'day' | 'week' | 'month' | 'quarter' | 'year'
}

export const walkRatioRowSchema = walkTimelineRowSchema
export type WalkRatioRow = z.infer<typeof walkRatioRowSchema>

export async function walkRatio(
  client: ApiClient,
  p: WalkRatioParams,
  signal?: AbortSignal,
): Promise<WalkRatioRow[]> {
  const raw = await client.rpc<unknown>(
    'walk_ratio',
    {
      p_ratio_kpi: p.kpi,
      p_startday: resolveStartday(p.from),
      p_endday: p.to,
      p_bucketsize: p.bucket_size ?? 'month',
      p_group_by: p.group_by ?? [],
      p_filter_dims: p.filter_dims ?? {},
    },
    { signal },
  )
  // ratios ignore dims a side can't carry (mig 0080) — render, never block.
  return z.array(walkRatioRowSchema).parse(raw)
}

export interface WalkRatioAbsoluteParams {
  kpi: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
  group_by?: string[]
}

export const walkRatioAbsoluteRowSchema = walkAbsoluteRowSchema
export type WalkRatioAbsoluteRow = z.infer<typeof walkRatioAbsoluteRowSchema>

export async function walkRatioAbsolute(
  client: ApiClient,
  p: WalkRatioAbsoluteParams,
  signal?: AbortSignal,
): Promise<WalkRatioAbsoluteRow[]> {
  const raw = await client.rpc<unknown>(
    'walk_ratio_absolute',
    {
      p_ratio_kpi: p.kpi,
      p_startday: resolveStartday(p.from),
      p_endday: p.to,
      p_group_by: p.group_by ?? [],
      p_filter_dims: p.filter_dims ?? {},
    },
    { signal },
  )
  // ratios ignore dims a side can't carry (mig 0080) — render, never block.
  return z.array(walkRatioAbsoluteRowSchema).parse(raw)
}

export interface WalkWrapperParams {
  fact_table: string
  kpi: string
  from: string
  to: string
  filter_dims?: Record<string, string[]>
  group_by?: string[]
  long_format_dims?: string[]
}

export const walkWrapperRowSchema = z.object({
  kpi_type: z.string(),
  time: z.string(),
  dim_name: z.string(),
  dim_value: z.string(),
  value: z.number(),
})
export type WalkWrapperRow = z.infer<typeof walkWrapperRowSchema>

export async function walkWrapper(
  client: ApiClient,
  p: WalkWrapperParams,
): Promise<WalkWrapperRow[]> {
  const raw = await client.rpc<unknown>('walk_wrapper', {
    p_fact_table: p.fact_table,
    p_kpi: p.kpi,
    p_startday: resolveStartday(p.from),
    p_endday: p.to,
    p_group_by: p.group_by ?? [],
    p_long_format_dims: p.long_format_dims ?? [],
    p_filter_dims: p.filter_dims ?? {},
  })
  return z.array(walkWrapperRowSchema).parse(raw)
}
